/* Public ZHELTA Services landing page.
 *
 * Mirrors the Apple-store-inspired layout from the standalone
 * cart-central shop while living natively at /services. Products are
 * fetched from cart-central's public product API and rendered with
 * ZHELTA's typography + colour language.
 *
 * Pricing rule (per product instructions):
 *   • If a product is free (price === 0), it stays free.
 *   • If a product price is over $700, we display +$45 baked into the
 *     price (handling fee). The displayed price is the only price the
 *     visitor ever sees.
 *   • Anything between $0 and $700 is shown at its raw price.
 *
 * Checkout always happens on cart-central; product cards deep-link out. */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShoppingBag, ArrowRight, Search, Star, ExternalLink,
  Cpu, Smartphone, Tablet, Monitor, Camera, Headphones, Boxes, Laptop,
  ShieldCheck, Truck, Wrench, BadgeCheck, History,
} from 'lucide-react';
import Header from '../components/ServicesHeader';
import Footer from '../components/ServicesFooter';
import DivisionStripBar from '../components/DivisionStripBar';
import AppearancePreFooter from '../components/AppearancePreFooter';
import SeoHead from '../components/SeoHead';
import CrossDivisionStrip from '../components/CrossDivisionStrip';
import RecommendedForYou from '../components/RecommendedForYou';
import { ZHELTA_SHOP_URL } from '../mock';
import { getRecentlyViewed } from '../lib/recentlyViewed';
import { useCurrency } from '../context/CurrencyContext';

const SHOP = ZHELTA_SHOP_URL.replace(/\/$/, '');
const FEE_THRESHOLD = 700;
const HANDLING_FEE = 45;

// 8 categories the shop returns. Icon + display name pairing kept here so
// we don't depend on the shop schema for visuals.
const CATEGORIES = [
  { id: 'laptops',          name: 'Laptops',          icon: Laptop },
  { id: 'desktops',         name: 'Desktops',         icon: Cpu },
  { id: 'phones',           name: 'Phones',           icon: Smartphone },
  { id: 'tablets',          name: 'Tablets',          icon: Tablet },
  { id: 'monitors',         name: 'Monitors',         icon: Monitor },
  { id: 'accessories',      name: 'Accessories',      icon: Headphones },
  { id: 'security-cameras', name: 'Security Cameras', icon: Camera },
  { id: 'software',         name: 'Software',         icon: Boxes },
];

/** Hook-based price formatter that converts a USD amount into the user's
 *  selected currency (via CurrencyContext, which caches live FX rates). */
function useDisplay() {
  const { format } = useCurrency();
  return React.useCallback((price) => {
    if (!price || price <= 0) return { value: 0, label: 'Free' };
    const adjusted = price > FEE_THRESHOLD ? price + HANDLING_FEE : price;
    return { value: adjusted, label: format(adjusted) };
  }, [format]);
}

const productHref = (slug) => `/store/p/${encodeURIComponent(slug)}`;

export default function Services() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCat, setActiveCat] = useState('all');
  const [search, setSearch] = useState('');
  const [recent, setRecent] = useState([]);

  // Read once on mount; no realtime sync (page is a single-tab experience).
  useEffect(() => { setRecent(getRecentlyViewed()); }, []);

  useEffect(() => {
    // Pull cart-central catalog AND ZHELTA-managed custom products in parallel.
    // Custom products carry `is_zhelta:true` so we can render a small badge
    // on them. They join the same grid + featured rail so the admin's choice
    // to mark something `featured` works for both sources.
    let cancelled = false;
    const ccUrl = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api/store/products`;
    const cc = fetch(ccUrl).then((r) => r.json()).catch(() => []);
    const zh = fetch(`${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api/services/custom-products`)
      .then((r) => r.json()).then((d) => (d?.rows || []).map((p) => ({
        // Map our schema -> the cart-central shape the rest of this page assumes.
        id: `zh-${p.slug}`, slug: p.slug, name: p.name, category: p.category,
        price: p.price, original_price: p.original_price, image: p.image,
        stock: p.stock, featured: p.featured, badge: p.badge,
        rating: p.rating, reviews: p.reviews, description: p.description,
        cta_url: p.cta_url, is_zhelta: true,
      }))).catch(() => []);
    Promise.all([cc, zh]).then(([ccRows, zhRows]) => {
      if (cancelled) return;
      const merged = [...zhRows, ...(Array.isArray(ccRows) ? ccRows : [])];
      setProducts(merged);
      setLoading(false);
    }).catch((e) => {
      if (!cancelled) { setError(e.message || 'Could not load products'); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, []);

  const featured = useMemo(() => products.filter((p) => p.featured).slice(0, 8), [products]);

  const visible = useMemo(() => {
    let out = products;
    if (activeCat !== 'all') out = out.filter((p) => p.category === activeCat);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter((p) => (p.name || '').toLowerCase().includes(q));
    }
    return out;
  }, [products, activeCat, search]);

  return (
    <div className="min-h-screen bg-[#F5F5F7] text-[#1d1d1f]">
      <SeoHead
        path="/services"
        title="ZHELTA Services — Verified Hardware & Gear"
        description="Laptops, peripherals, networking, and tools — vetted and shipped from one shop."
      />
      <Header />
      <DivisionStripBar current="services" />

      {/* ----- Hero ----- */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#FAFAFA] to-white pt-12 md:pt-20 pb-16 md:pb-24"
        data-testid="services-hero">
        <div className="zh-container text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-zinc-300 bg-zinc-100 px-5 py-2 mb-6">
            <ShoppingBag className="h-3.5 w-3.5 text-zinc-700" />
            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-zinc-800">
              ZHELTA Services Store
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-[3.75rem] font-extrabold leading-[1.05] tracking-tight mb-5">
            <span className="block">Verified hardware,</span>
            <span className="block text-zinc-900">
              shipped from people you trust.
            </span>
          </h1>
          <p className="text-base md:text-[17px] text-[#6E6E73] max-w-2xl mx-auto mb-8">
            Curated laptops, phones, tablets, monitors, and security gear — backed
            by ZHELTA Systems for setup, support, and warranty handling.
          </p>

          {/* Search */}
          <form onSubmit={(e) => e.preventDefault()}
            className="max-w-[480px] mx-auto relative" role="search">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E6E73]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products"
              data-testid="services-search"
              className="w-full pl-11 pr-4 py-3 rounded-full border border-[#E5E5E7] bg-white text-[14px] outline-none focus:border-[#c9a227] focus:ring-4 focus:ring-[#c9a227]/10 transition-all"
            />
          </form>
        </div>
      </section>

      {/* ----- Trust strip: Why buy from ZHELTA Services ----- */}
      <section
        className="border-y border-[#F0EDE5] bg-white py-8 md:py-10"
        data-testid="services-trust-strip"
      >
        <div className="zh-container">
          <div className="mb-5 md:mb-6 flex items-end justify-between flex-wrap gap-3">
            <div>
              <span className="text-[10.5px] font-bold uppercase tracking-[0.28em] text-zinc-800">
                Why buy from ZHELTA Services
              </span>
              <h2 className="text-[20px] md:text-[24px] font-extrabold mt-1">
                One vendor. End-to-end accountability.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {[
              { Icon: BadgeCheck, title: 'Verified hardware', body: 'Every SKU is vetted by ZHELTA Systems engineers before it lists.' },
              { Icon: Wrench,     title: 'Setup & support',   body: 'White-glove deployment, imaging and onboarding handled by our team.' },
              { Icon: ShieldCheck,title: 'Warranty handled',  body: 'We chase the manufacturer for repairs and replacements — you don\u2019t.' },
              { Icon: Truck,      title: 'Direct shipping',   body: 'Fulfilled from trusted distributors with tracked delivery to your door.' },
            ].map(({ Icon, title, body }) => (
              <div
                key={title}
                data-testid={`services-trust-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="rounded-2xl border border-[#E5E5E7] bg-[#FAFAFA] p-4 md:p-5 transition-colors hover:border-zinc-400 hover:bg-zinc-100"
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-zinc-100 text-[#c9a227] mb-3">
                  <Icon className="h-4 w-4" strokeWidth={2} />
                </span>
                <div className="text-[13.5px] md:text-[14px] font-extrabold text-[#0e0e10] mb-1">{title}</div>
                <p className="text-[12.5px] leading-snug text-[#6E6E73]">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ----- Recently viewed (only if user has any) ----- */}
      {activeCat === 'all' && !search && recent.length > 0 && (
        <section
          className="py-10 md:py-12 bg-white"
          data-testid="services-recently-viewed"
        >
          <div className="zh-container">
            <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-zinc-100 text-[#c9a227]">
                  <History className="h-4 w-4" strokeWidth={2} />
                </span>
                <div>
                  <span className="text-[10.5px] font-bold uppercase tracking-[0.28em] text-zinc-800">
                    Recently viewed
                  </span>
                  <h2 className="text-[20px] md:text-[22px] font-extrabold mt-0.5">
                    Pick up where you left off.
                  </h2>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {recent.map((p) => <ProductCard key={p.slug} p={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* ----- Categories grid (Apple-style) ----- */}
      <section className="py-10 md:py-14 bg-[#FAFAFA] border-y border-[#F0EDE5]"
        data-testid="services-categories">
        <div className="zh-container">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
              <span className="text-[10.5px] font-bold uppercase tracking-[0.28em] text-zinc-800">Shop by category</span>
              <h2 className="text-[24px] md:text-[28px] font-extrabold mt-1">Find what you need fast.</h2>
            </div>
            <button
              type="button"
              onClick={() => { setActiveCat('all'); setSearch(''); }}
              data-testid="services-show-all"
              className={`text-[12px] font-bold uppercase tracking-[0.18em] inline-flex items-center gap-1.5 transition-colors
                ${activeCat === 'all' && !search ? 'text-zinc-700' : 'text-[#6E6E73] hover:text-zinc-700'}`}>
              View all <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <ul className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              const active = activeCat === c.id;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setActiveCat(c.id)}
                    data-testid={`services-cat-${c.id}`}
                    className={`group w-full h-full rounded-2xl border bg-white p-4 text-center transition-all hover:-translate-y-0.5
                      ${active
                        ? 'border-[#c9a227] shadow-[0_10px_25px_-12px_rgba(201,162,39,0.45)] bg-zinc-100'
                        : 'border-[#E5E5E7] hover:border-zinc-400 hover:shadow-[0_10px_25px_-15px_rgba(201,162,39,0.25)]'}`}>
                    <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl mb-2 transition-colors
                      ${active ? 'bg-[#c9a227] text-white' : 'bg-zinc-100 text-zinc-800'}`}>
                      <Icon className="h-4 w-4" strokeWidth={2} />
                    </span>
                    <div className="text-[12.5px] font-bold leading-tight">{c.name}</div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* ----- Featured (Apple "Get the latest" strip) ----- */}
      {activeCat === 'all' && !search && featured.length > 0 && (
        <section className="py-12 md:py-16" data-testid="services-featured">
          <div className="zh-container">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
              <div>
                <span className="text-[10.5px] font-bold uppercase tracking-[0.28em] text-zinc-800">Featured</span>
                <h2 className="text-[24px] md:text-[28px] font-extrabold mt-1">The latest from ZHELTA Services.</h2>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {featured.map((p) => <ProductCard key={p.id} p={p} large />)}
            </div>
          </div>
        </section>
      )}

      {/* ----- Catalog grid ----- */}
      <section className="py-12 md:py-16 bg-[#FAFAFA] border-t border-[#F0EDE5]"
        data-testid="services-catalog">
        <div className="zh-container">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
              <span className="text-[10.5px] font-bold uppercase tracking-[0.28em] text-zinc-800">
                {activeCat === 'all' ? 'All products' : (CATEGORIES.find((c) => c.id === activeCat)?.name || 'Products')}
              </span>
              <h2 className="text-[24px] md:text-[28px] font-extrabold mt-1">
                {visible.length} item{visible.length === 1 ? '' : 's'}
              </h2>
            </div>
            <Link to="/store/products"
              data-testid="services-open-shop"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10]"
              style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }}>
              View all products <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="rounded-2xl bg-white border border-[#E5E5E7] aspect-[4/5] animate-pulse" />
              ))}
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
              <div className="text-[14px] font-bold text-red-800">Could not load products</div>
              <p className="text-[12.5px] text-red-700 mt-1">{error}</p>
              <Link to="/store/products"
                className="inline-flex items-center gap-1.5 mt-3 text-[12px] font-bold text-red-800 underline">
                Browse the catalog →
              </Link>
            </div>
          ) : visible.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#E5E5E7] bg-white p-12 text-center">
              <ShoppingBag className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <div className="text-[15px] font-bold">No products match.</div>
              <p className="text-[12.5px] text-[#6E6E73]">Try a different category or clear your search.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {visible.map((p) => <ProductCard key={p.id} p={p} />)}
            </div>
          )}
        </div>
      </section>

      <RecommendedForYou division="services" heading="What goes well with what you bought" />
      <CrossDivisionStrip hide={['services']} />
      <AppearancePreFooter />
      <Footer />
    </div>
  );
}

function ProductCard({ p, large = false }) {
  const display = useDisplay();
  const cur = display(p.price);
  const orig = p.original_price && p.original_price > p.price ? display(p.original_price) : null;
  return (
    <Link
      to={productHref(p.slug)}
      data-testid={`services-product-${p.slug}`}
      className="group block rounded-2xl bg-white border border-[#E5E5E7] overflow-hidden transition-all hover:-translate-y-1 hover:border-zinc-400 hover:shadow-[0_20px_45px_-25px_rgba(201,162,39,0.45)]"
    >
      <div className="relative aspect-square bg-[#FAFAFA] overflow-hidden">
        {p.image ? (
          <img
            src={p.image}
            alt={p.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#9aa0a6]">
            <ShoppingBag className="w-8 h-8" />
          </div>
        )}
        {p.badge && (() => {
          const isNew = String(p.badge).trim().toLowerCase() === 'new';
          return (
            <span
              className={`absolute top-3 left-3 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                isNew ? 'text-[#0e0e10]' : 'bg-zinc-200 text-zinc-900'
              }`}
              style={
                isNew
                  ? { background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }
                  : undefined
              }
            >
              {p.badge}
            </span>
          );
        })()}
        {p.is_zhelta && (
          <span className="absolute top-3 right-3 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-[#0e0e10]"
            style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}
            title="ZHELTA-managed product">
            ZHELTA
          </span>
        )}
      </div>
      <div className={`p-${large ? 5 : 4}`}>
        <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-zinc-800 mb-1 truncate">
          {p.category?.replace('-', ' ') || 'Product'}
        </div>
        <h3 className={`font-extrabold text-[#0e0e10] truncate ${large ? 'text-[16px]' : 'text-[14px]'}`}>
          {p.name}
        </h3>
        <div className="flex items-center gap-1 mt-1 mb-2">
          {p.rating ? (
            <>
              <Star className="w-3 h-3 fill-[#f5a623] text-zinc-700" />
              <span className="text-[11px] font-semibold text-[#0e0e10]">{p.rating}</span>
              <span className="text-[10.5px] text-[#9aa0a6]">({p.reviews || 0})</span>
            </>
          ) : (
            <span className="text-[10.5px] text-[#9aa0a6]">No reviews yet</span>
          )}
        </div>
        <div className="flex items-baseline gap-2">
          <span className={`font-extrabold text-[#0e0e10] tabular-nums ${large ? 'text-[18px]' : 'text-[15.5px]'}`}>
            {cur.label}
          </span>
          {orig && (
            <span className="text-[12px] text-[#9aa0a6] line-through tabular-nums">{orig.label}</span>
          )}
        </div>
      </div>
    </Link>
  );
}
